import { PropsWithChildren, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/routes/use_auth'

type NavKey = 'home' | 'program' | 'about' | 'why' | 'funGame' | 'article' | 'faq' | 'contact'

type Props = PropsWithChildren<{
	activeNav?: NavKey
	topBackground?: string
}>

const getCopy = (lang: string) => {
	if (lang === 'en') {
		return {
			home: 'Home',
			program: 'Program',
			about: 'About',
			why: 'Why Choose Us',
			funGame: 'Fun Game',
			article: 'Article',
			faq: 'FAQ',
			contact: 'Contact Us',
			login: 'Login',
			register: 'Sign Up',
			dashboard: 'Home',
			brand: 'Mie Ayam Gaga',
			tagline:
				"GAGA Mie Ayam is not just about regular instant noodles; it's about crafting a special and authentic experience.",
			pagesTitle: 'Pages',
			contactTitle: 'Contact',
			followTitle: 'Follow Us',
			address:
				'Jl. Sinpas Raya No.15, Kby. Lama Sel., Kec. Kebayoran Lama, Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12240',
			email: 'info@jakaranatama.co.id',
			phone: '0811-9690-8398',
			copyright: '©2026 Mie Ayam Gaga. All right reserved.'
		}
	}
	return {
		home: 'Home',
		program: 'Program',
		about: 'About',
		why: 'Why Choose Us',
		funGame: 'Fun Game',
		article: 'Article',
		faq: 'FAQ',
		contact: 'Contact Us',
		login: 'Login',
		register: 'Sign Up',
		dashboard: 'Beranda',
		brand: 'Mie Ayam Gaga',
		tagline:
			'GAGA Mie Ayam bukan sekadar mie instan biasa; ini tentang menghadirkan pengalaman mie ayam yang spesial dan autentik.',
		pagesTitle: 'Pages',
		contactTitle: 'Contact',
		followTitle: 'Follow Us',
		address:
			'Jl. Sinpas Raya No.15, Kby. Lama Sel., Kec. Kebayoran Lama, Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12240',
		email: 'info@jakaranatama.co.id',
		phone: '0811-9690-8398',
		copyright: '©2026 Mie Ayam Gaga. All right reserved.'
	}
}

export const LandingChrome = ({ activeNav, topBackground, children }: Props) => {
	const navigate = useNavigate()
	const { i18n } = useTranslation()
	const { isLoggedIn } = useAuth()
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

	const copy = getCopy(i18n.language)

	const goHomeAnchor = (anchor: NavKey) => {
		navigate(`/#${anchor}`)
	}

	const navItems: { key: NavKey; label: string; onClick: () => void }[] = [
		{ key: 'home', label: copy.home, onClick: () => navigate('/') },
		{ key: 'program', label: copy.program, onClick: () => goHomeAnchor('program') },
		{ key: 'about', label: copy.about, onClick: () => goHomeAnchor('about') },
		{ key: 'why', label: copy.why, onClick: () => goHomeAnchor('why') },
		{ key: 'funGame', label: copy.funGame, onClick: () => navigate('/fun-game') },
		{ key: 'article', label: copy.article, onClick: () => navigate('/article') },
		{ key: 'faq', label: copy.faq, onClick: () => goHomeAnchor('faq') },
		{ key: 'contact', label: copy.contact, onClick: () => goHomeAnchor('contact') }
	]

	return (
		<div className="relative min-h-screen w-full overflow-x-clip">
			{/* Optional top background (shows behind transparent header + first section) */}
			{topBackground && (
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 h-[560px] md:h-[720px] z-0"
					style={{ background: topBackground }}
				/>
			)}
			{/* Header */}
			<header
				className={
					topBackground
						? 'sticky top-0 z-50 bg-transparent'
						: 'sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100'
				}
			>
				<div className="mx-auto flex max-w-[1320px] items-center gap-6 px-4 min-[375px]:px-6 md:px-10 py-4">
					<button
						type="button"
						onClick={() => navigate('/')}
						className="shrink-0 transition-opacity hover:opacity-80"
					>
						<img
							src={`${process.env.PUBLIC_URL}/logo-gaga.png`}
							alt="GAGA"
							className="h-8 md:h-10 w-auto"
						/>
					</button>

					{/* Desktop nav */}
					<nav className="hidden lg:flex flex-1 items-center justify-center gap-8 text-sm">
						{navItems.map(item => (
							<button
								key={item.key}
								type="button"
								onClick={item.onClick}
								className={`shrink-0 font-medium transition-colors ${
									activeNav === item.key
										? 'text-blue-600 font-bold'
										: 'text-gray-500 hover:text-blue-600'
								}`}
							>
								{item.label}
							</button>
						))}
					</nav>

					<div className="hidden lg:flex items-center gap-3 ml-auto">
						{isLoggedIn() ? (
							<button
								type="button"
								onClick={() => navigate('/app')}
								className="rounded-full bg-[#283A97] px-8 py-2.5 text-sm font-bold text-white shadow-md hover:bg-blue-800 transition-colors"
							>
								{copy.dashboard}
							</button>
						) : (
							<>
								<button
									type="button"
									onClick={() => navigate('/login')}
									className="flex items-center justify-center gap-2 rounded-[56px] border border-[#283A97] px-6 py-2 text-sm font-bold text-[#283A97] transition-all hover:bg-blue-50/50"
								>
									{copy.login}
								</button>
								<button
									type="button"
									onClick={() => navigate('/register')}
									className="flex items-center justify-center gap-2 rounded-[56px] bg-[#283A97] px-6 py-2 text-sm font-bold text-white shadow-md transition-all hover:bg-blue-800"
								>
									{copy.register}
								</button>
							</>
						)}
					</div>

					{/* Mobile hamburger */}
					<button
						type="button"
						className="ml-auto flex lg:hidden h-10 w-10 items-center justify-center"
						onClick={() => setIsMenuOpen(true)}
						aria-label="Menu"
					>
						<img
							src={require('@/assets/icons/hamburger.png')}
							alt="Menu"
							className="h-full w-full object-contain"
						/>
					</button>
				</div>
			</header>

			{/* Mobile menu overlay */}
			{isMenuOpen && (
				<div
					className="fixed top-0 left-0 right-0 bottom-0 z-[200] bg-white lg:hidden"
					style={{ height: '100dvh' }}
				>
					<div className="flex flex-col" style={{ height: '100dvh' }}>
						<div className="flex items-center justify-between px-6 py-4 shrink-0">
							<button
								type="button"
								onClick={() => {
									navigate('/')
									setIsMenuOpen(false)
								}}
								className="shrink-0 transition-opacity hover:opacity-80"
							>
								<img
									src={`${process.env.PUBLIC_URL}/logo-gaga.png`}
									alt="GAGA"
									className="h-8 w-auto"
								/>
							</button>
							<button
								type="button"
								className="flex h-12 w-12 items-center justify-center"
								onClick={() => setIsMenuOpen(false)}
								aria-label="Close"
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#283A97] bg-white shadow-sm text-[#283A97]">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<line x1="18" y1="6" x2="6" y2="18"></line>
										<line x1="6" y1="6" x2="18" y2="18"></line>
									</svg>
								</div>
							</button>
						</div>
						<div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4">
							<nav className="flex flex-col gap-6 text-xl font-bold text-gray-800">
								{navItems.map(item => (
									<button
										key={item.key}
										type="button"
										className={`text-left ${
											activeNav === item.key ? 'text-blue-600' : ''
										}`}
										onClick={() => {
											setIsMenuOpen(false)
											item.onClick()
										}}
									>
										{item.label}
									</button>
								))}
							</nav>
						</div>
						<div className="shrink-0 flex flex-col gap-4 px-6 pb-8 pt-4">
							{isLoggedIn() ? (
								<button
									type="button"
									onClick={() => {
										navigate('/app')
										setIsMenuOpen(false)
									}}
									className="w-full rounded-xl bg-[#283A97] py-4 text-center font-bold text-white shadow-lg"
								>
									{copy.dashboard}
								</button>
							) : (
								<>
									<button
										type="button"
										onClick={() => {
											navigate('/login')
											setIsMenuOpen(false)
										}}
										className="w-full rounded-xl border border-[#283A97] py-4 text-center font-bold text-[#283A97]"
									>
										{copy.login}
									</button>
									<button
										type="button"
										onClick={() => {
											navigate('/register')
											setIsMenuOpen(false)
										}}
										className="w-full rounded-xl bg-[#283A97] py-4 text-center font-bold text-white shadow-lg"
									>
										{copy.register}
									</button>
								</>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Main content */}
			<main className="relative z-10">{children}</main>

			{/* Footer */}
			<footer
				className="relative z-10 pt-16 pb-8 md:pt-24"
				style={{
					background: 'linear-gradient(180deg, #FFFFFF 0%, #D7EAFE 100%)'
				}}
			>
				<div className="mx-auto max-w-[1320px] px-4 min-[375px]:px-6 md:px-10">
					<div className="grid gap-12 lg:grid-cols-4 lg:gap-8">
						<div className="flex flex-col gap-6">
							<div className="flex items-center gap-3">
								<img
									src={`${process.env.PUBLIC_URL}/logo-gaga.png`}
									alt="GAGA"
									className="h-8 w-auto"
								/>
								<span className="text-xl font-bold text-[#231F20]">
									{copy.brand}
								</span>
							</div>
							<p className="max-w-xs text-sm leading-relaxed text-[#918D8E]">
								{copy.tagline}
							</p>
						</div>

						<div className="flex flex-col gap-6">
							<h4 className="text-base font-bold text-[#231F20]">
								{copy.pagesTitle}
							</h4>
							<ul className="flex flex-col gap-4 text-sm text-[#918D8E]">
								<li>
									<button
										type="button"
										onClick={() => navigate('/')}
										className="hover:text-[#1B81DC] transition-colors"
									>
										{copy.home}
									</button>
								</li>
								<li>
									<button
										type="button"
										onClick={() => goHomeAnchor('program')}
										className="hover:text-[#1B81DC] transition-colors"
									>
										{copy.program}
									</button>
								</li>
								<li>
									<button
										type="button"
										onClick={() => goHomeAnchor('about')}
										className="hover:text-[#1B81DC] transition-colors"
									>
										{copy.about}
									</button>
								</li>
								<li>
									<button
										type="button"
										onClick={() => navigate('/fun-game')}
										className="hover:text-[#1B81DC] transition-colors"
									>
										{copy.funGame}
									</button>
								</li>
								<li>
									<button
										type="button"
										onClick={() => goHomeAnchor('faq')}
										className="hover:text-[#1B81DC] transition-colors"
									>
										{copy.faq}
									</button>
								</li>
								<li>
									<button
										type="button"
										onClick={() => goHomeAnchor('contact')}
										className="hover:text-[#1B81DC] transition-colors"
									>
										{copy.contact}
									</button>
								</li>
							</ul>
						</div>

						<div className="flex flex-col gap-6">
							<h4 className="text-base font-bold text-[#231F20]">
								{copy.contactTitle}
							</h4>
							<div className="flex flex-col gap-4 text-sm text-[#918D8E]">
								<p className="leading-relaxed">{copy.address}</p>
								<div className="flex items-center gap-3">
									<div className="flex h-6 w-6 items-center justify-center rounded-md">
										<img
											src={require('@/assets/icons/mail.png')}
											alt="Email"
											className="h-full w-full object-contain"
										/>
									</div>
									<span>{copy.email}</span>
								</div>
								<div className="flex items-center gap-3">
									<div className="flex h-6 w-6 items-center justify-center rounded-md">
										<img
											src={require('@/assets/icons/phone.png')}
											alt="Phone"
											className="h-full w-full object-contain"
										/>
									</div>
									<div className="flex flex-col">
										<span>{copy.phone}</span>
										<span className="text-xs text-[#B0ACAD]">Available During Office Hours</span>
									</div>
								</div>
							</div>
						</div>

						<div className="flex flex-col gap-6">
							<h4 className="text-base font-bold text-[#231F20]">
								{copy.followTitle}
							</h4>
							<div className="flex gap-4">
								{[
									{
										name: 'tiktok',
										icon: require('@/assets/icons/tiktok.png'),
										href: 'https://www.tiktok.com/@gaga_mieayam'
									},
									{
										name: 'instagram',
										icon: require('@/assets/icons/instagram.png'),
										href: 'https://www.instagram.com/gaga_mieayam'
									}
								].map(social => (
									<a
										key={social.name}
										href={social.href}
										target="_blank"
										rel="noopener noreferrer"
										className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 transition-all hover:bg-[#1B81DC] hover:border-[#1B81DC]"
									>
										<img
											src={social.icon}
											alt={social.name}
											className="h-5 w-5 object-contain"
										/>
									</a>
								))}
							</div>
						</div>
					</div>

					<div className="mt-16 border-t border-gray-200 pt-8 text-center text-sm text-[#918D8E]">
						<div>{copy.copyright}</div>
						<div className="mt-1">
							Developed by{' '}
							<a
								href="https://loyal.id"
								target="_blank"
								rel="noopener"
								title="Loyal.id — Loyalty Program Platform Indonesia"
								className="hover:text-[#1B81DC] transition-colors"
							>
								Loyal.id
							</a>
						</div>
					</div>
				</div>
			</footer>
		</div>
	)
}
